/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from "react";
import { EthPrice, FpPrice, Pet } from "../../../common/types";
import { WalletSelector } from "./WalletSelector";
import { sortPets } from "../../helpers/helpers";

function ToggleBox({
  selectedViewer,
  setSelectedViewer,
  isDarkMode,
}: {
  selectedViewer: string;
  setSelectedViewer: (value: string) => void;
  isDarkMode: boolean;
}) {

  const isPetPassSelected = selectedViewer === "petPasses";
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedViewer(e.target.checked ? "petPasses" : "timerViewer");
  };

  return (
    <div className={`${isPetPassSelected ? "" : "mr-3"} flex items-center`}>
      <label className="flex cursor-pointer items-center text-sm">
        <input
          type="checkbox"
          checked={selectedViewer === "petPasses"}
          onChange={handleCheckboxChange}
          className="peer sr-only"
        />
        <div
          className={`h-4 w-4 rounded border ${
            isDarkMode
              ? "border-darkgreen bg-darkblue peer-checked:bg-darkgreen"
              : "border-brown bg-white peer-checked:bg-brown"
          }`}
        ></div>
      </label>
    </div>
  );
}

export default ToggleBox;


function ToggleSwitch({
  selectedViewer,
  setSelectedViewer,
  isDarkMode,
}: {
  selectedViewer: string;
  setSelectedViewer: (value: string) => void;
  isDarkMode: boolean;
}) {
  const isTimersSelected = selectedViewer === "statViewer";

  return (
    <div className="flex items-center">
      <div
        className={`relative h-6 w-10 rounded-full border-2 ${
          isDarkMode ? "border-darkgreen bg-darkblue" : "border-brown bg-white"
        }`}
        onClick={() => setSelectedViewer(isTimersSelected ? "timerViewer" : "statViewer")}
      >
        <div
          className={`m-[.135rem] h-4 w-4 rounded-full ${
            isDarkMode ? "bg-darkgreen" : "bg-brown"
          } ${isTimersSelected ? "translate-x-full transform" : ""}`}
        ></div>
      </div>
    </div>
  );
}

export function TopBar({
  pets,
  setPets,
  selectedViewer,
  setSelectedViewer,
  walletAddress,
  setWalletAddress,
  isLoading,
  setRefreshTrigger,
  setIsLoading,
  sortKey,
  setSortKey,
  sortOrder,
  setSortOrder,
  isPhone,
  fpPrices,
  ethPrices,
  isDarkMode,
}: {
  pets: Pet[];
  setPets: React.Dispatch<React.SetStateAction<Pet[]>>;
  selectedViewer: string;
  setSelectedViewer: (value: string) => void;
  walletAddress: string;
  setWalletAddress: (address: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  sortKey: keyof Pet;
  setSortKey: React.Dispatch<React.SetStateAction<keyof Pet>>;
  sortOrder: "asc" | "desc";
  setSortOrder: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
  isPhone: boolean;
  fpPrices: FpPrice[];
  ethPrices: EthPrice[];
  isDarkMode: boolean;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isPetPassView, setIsPetPassView] = useState(false);
  const isTouchDevice = "ontouchstart" in window;
  const menuIconPath = "/imgs/menu.svg";
  const closeIconPath = "/imgs/close.svg";
  const menuIconPathDark = "/imgs/menuDark.svg";
  const closeIconPathDark = "/imgs/closeDark.svg";
  const svgMap = {
    toppets: isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg",
    topcats: isDarkMode ? "/imgs/catDark.svg" : "/imgs/cat.svg",
    topattack: isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg",
    topdefense: isDarkMode ? "/imgs/shieldDark.svg" : "/imgs/shield.svg",
    topoldpets: isDarkMode ? "/imgs/hourglassDark.svg" : "/imgs/hourglass.svg",
    charts: isDarkMode ? "/imgs/chartDark.svg" : "/imgs/chart.svg",
    activityfeed: isDarkMode ? "/imgs/rssDark.svg" : "/imgs/rss.svg",
    store: isDarkMode ? "/imgs/storeDark.svg" : "/imgs/store.svg",
    legend: isDarkMode ? "/imgs/legendDark.svg" : "/imgs/legend.svg",
    donate: isDarkMode ? "/imgs/heartDark.svg" : "/imgs/heart.svg",
    stats: isDarkMode ? "/imgs/passBadgeDark.png" : "/imgs/passBadge.png",
    changelog: isDarkMode ? "/imgs/clipboardDark.svg" : "/imgs/clipboard.svg",
    topstars: isDarkMode ? "/imgs/starDark.svg" : "/imgs/star.svg",
    topdragons: isDarkMode ? "/imgs/dragonDark.svg" : "/imgs/dragon.svg",
    topdogs: isDarkMode ? "/imgs/dogDark.svg" : "/imgs/dog.svg",
    topsheep: isDarkMode ? "/imgs/sheepDark.svg" : "/imgs/sheep.svg",
    toppepes: isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg",
    topmonkeys: isDarkMode ? "/imgs/monkeyDark.svg" : "/imgs/monkey.svg",
    toppenguins: isDarkMode ? "/imgs/penguinDark.svg" : "/imgs/penguin.svg",
    toppandas: isDarkMode ? "/imgs/pandaDark.svg" : "/imgs/panda.svg",
    profile: isDarkMode ? "/imgs/profileDark.svg" : "/imgs/profile.svg",
    settings: isDarkMode ? "/imgs/settingsDark.svg" : "/imgs/settings.svg",
    resources: isDarkMode ? "/imgs/toolboxDark.svg" : "/imgs/toolbox.svg",
    calculator: isDarkMode ? "/imgs/calculatorDark.svg" : "/imgs/calculator.svg",
    downloads: isDarkMode ? "/imgs/downloadDark.svg" : "/imgs/download.svg",
    whitelist: isDarkMode ? "/imgs/awardDark.svg" : "/imgs/award.svg",
  };

  let viewText;
  switch (selectedViewer) {
    case "timerViewer":
      viewText = "Pet Timers";
      break;
    case "statViewer":
      viewText = "Pet Stats";
      break;
    case "toppets":
      viewText = "Top 100 Pets";
      break;
    case "topcats":
      viewText = "Top Cats";
      break;
    case "topoldpets":
      viewText = "Top 100 Old Pets";
      break;
    case "charts":
      viewText = "Charts";
      break;
    case "activityfeed":
      viewText = "Activity Feed";
      break;
    case "store":
      viewText = "Fren Shop";
      break;
    case "legend":
      viewText = "Legend";
      break;
    case "donate":
      viewText = "Donate";
      break;
    case "stats":
      viewText = "Stats";
      break;
    case "changelog":
      viewText = "Change Log";
      break;
    case "topstars":
      viewText = "Top 100 Keepers";
      break;
    case "topdragons":
      viewText = "Dragons";
      break;
    case "topsheep":
      viewText = "Top 100 Sheep";
      break;
    case "topdogs":
      viewText = "Top 100 Dogs";
      break;
    case "toppandas":
      viewText = "Top Pandas";
      break;
    case "topmonkeys":
      viewText = "Top Monkeys";
      break;
    case "toppepes":
      viewText = "Top Pepes";
      break;
    case "toppenguins":
      viewText = "Top Penguins";
      break;
    case "profile":
      viewText = "Profile";
      break;
    case "settings":
      viewText = "Settings";
      break;
    case "resources":
      viewText = "Resources";
      break;
    case "petPasses":
      viewText = "Pet Licences";
      break;
    case "calculator":
      viewText = "EV Calculator";
      break;
    case "downloads":
      viewText = "Downloads";
      break;
    case "whitelist":
      viewText = "Whitelist";
      break;
    default:
      viewText = "View";
  }

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  useEffect(() => {
    setPets((prevPets) => sortPets(prevPets, sortKey, sortOrder));
  }, [sortKey, sortOrder]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    setSortKey("score");
    setSortOrder("desc");
  }, [walletAddress]);

  const handleScroll = useCallback(() => {
    if (window.scrollY > 0 && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const refreshButtonView = ["statViewer", "timerViewer", "petPasses"].includes(selectedViewer);

  const hideOnView = [
    "toppets",
    "topattack",
    "topdefense",
    "charts",
    "topcats",
    "activityfeed",
    "legend",
    "donate",
    "topoldpets",
    "store",
    "stats",
    "changelog",
    "topstars",
    "topdragons",
    "toppepes",
    "topsheep",
    "toppandas",
    "topmonkeys",
    "topdogs",
    "toppenguins",
    "profile",
    "settings",
    "resources",
    "calculator",
    "downloads",
    "whitelist",
  ].includes(selectedViewer);

  const hidePriceOnView = [
    "timerViewer",
    "statViewer",
    "petPasses",
    "legend",
    "donate",
    "changelog",
    "topoldpets",
    "topcats",
    "toppets",
    "topattack",
    "topdefense",
    "topdragons",
    "toppepes",
    "topsheep",
    "toppandas",
    "topmonkeys",
    "topdogs",
    "toppenguins",
    "topstars",
    "resources",
    "settings",
    "downloads",
    "whitelist",
  ];

  const showPrices = !hidePriceOnView.includes(selectedViewer);
  const latestFpPrice =
    fpPrices.length > 0 ? `$${fpPrices[fpPrices.length - 1].fp_price.toFixed(2)}` : "";
  const latestEthPrice =
    ethPrices.length > 0 ? `$${ethPrices[ethPrices.length - 1].eth_price.toFixed(0)}` : "";

  return (
    <div className="fixed z-40 w-full">
      <header
        className={`relative z-40 flex h-16 justify-between pb-1 ${isDarkMode ? "bg-darkblack" : "bg-green"} `}
      >
        <img
          src={isDarkMode ? "/imgs/fpAnalyticaLogoBD.png" : "/imgs/fpAnalyticaLogoBD.png"}
          alt=""
          className="mt-2 h-14 w-14"
          onClick={() => {
                const savedDefault = localStorage.getItem("defaultView") || "timerViewer";
                setSelectedViewer(savedDefault);
              }}
        />
        <WalletSelector
          pets={pets}
          walletAddress={walletAddress}
          setWalletAddress={setWalletAddress}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          isPhone={isPhone}
          isDarkMode={isDarkMode}
        />
        <img
          src={
            isDarkMode
              ? isMenuOpen
                ? closeIconPathDark
                : menuIconPathDark
              : isMenuOpen
                ? closeIconPath
                : menuIconPath
          }
          alt={isMenuOpen ? "Close Menu" : "Open Menu"}
          className="mr-2 mt-[1.55rem] h-6 w-6 cursor-pointer"
          onClick={() => setIsMenuOpen((prev) => !prev)}
        />
      </header>

      <div
        className={`relative top-0  z-20 mx-auto flex flex-col justify-center border-b-2 border-t-2 pb-1 shadow-lg ${isDarkMode ? "border-darkgreen bg-darkblack text-white" : "border-brown bg-green"}`}
        style={{ minWidth: "390px" }}
      >
        <div
          className={`mb-1 ml-5 mr-4 mt-2 flex items-center text-lg font-semibold ${selectedViewer === "statViewer" || selectedViewer === "timerViewer" || selectedViewer === "petPasses" ? "justify-between" : "justify-center"}`}
        >
          <div className="flex flex-row">
            {showPrices && (
              <>
                <div className="mr-6 flex flex-row text-sm">
                  <img
                    src={isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg"}
                    alt="FP Price"
                    className="mr-[0.25rem] mt-[.45rem] h-4 w-4"
                  />
                  <div className={`mt-[.3rem] ${isDarkMode ? "text-white" : "text-brown"}`}>
                    {latestFpPrice}
                  </div>
                </div>
              </>
            )}
            {viewText}
            {svgMap[selectedViewer as keyof typeof svgMap] && (
              <img
                src={svgMap[selectedViewer as keyof typeof svgMap]}
                alt={viewText}
                className={`ml-2 mr-1 mt-[.2rem] h-5 w-5 ${selectedViewer === "topattack" ? "scale-x-[-1] transform" : ""}`}
              />
            )}
            {refreshButtonView && (
              <button
                className={`btn-active ml-2 mt-[0.1rem] h-[1.55rem] w-10 rounded-md border-2 ${isDarkMode ? "border-darkgreen bg-darkblue" : "border-brown bg-white"}`}
                onClick={() => setRefreshTrigger((prev) => prev + 1)}
              >
                {isLoading && pets.length !== 0 && !isPhone ? (
                  <img
                    src={isDarkMode ? "/imgs/refreshDark.svg" : "/imgs/refresh.svg"}
                    alt=""
                    className="spin h-4 w-12"
                  />
                ) : (
                  <img
                    src={isDarkMode ? "/imgs/refreshDark.svg" : "/imgs/refresh.svg"}
                    alt=""
                    className="h-4 w-12"
                  />
                )}
              </button>
            )}
            {showPrices && (
              <div className="ml-6 flex flex-row text-sm">
                <img
                  src={isDarkMode ? "/imgs/ethereumDark.svg" : "/imgs/ethereum.svg"}
                  alt="ETH Price"
                  className="mt-[.4rem] h-4 w-4"
                />
                <div className={`mt-[.3rem] ${isDarkMode ? "text-white" : "text-brown"}`}>
                  {latestEthPrice}
                </div>
              </div>
            )}
          </div>
          {!hideOnView && (
            <div className="row flex">
              <ToggleBox
                  selectedViewer={selectedViewer}
                  setSelectedViewer={setSelectedViewer}
                  isDarkMode={isDarkMode}
                />

                {selectedViewer !== "petPasses" && (
                  <ToggleSwitch
                    selectedViewer={selectedViewer}
                    setSelectedViewer={setSelectedViewer}
                    isDarkMode={isDarkMode}
                  />
                )}

              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as keyof Pet)}
                className={`ml-[.55rem] max-w-20 rounded-md border-2 ${isDarkMode ? "[box-shadow:inset_0_-2px_#37b764 border-darkgreen bg-darkblue text-white" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"} text-sm`}
                style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
              >
                <optgroup label="General">
                  <option value="score">Score</option>
                  <option value="id">Id</option>
                  <option value="name">Name</option>
                  <option value="petAgeInDays">Age</option>
                  <option value="petType">Dna</option>
                  <option value="rewards">Rewards</option>
                  <option value="stars">Stars</option>
                  <option value="actualCarrots">Carrots</option>
                  <option value="availableGacha">Available Gacha</option>
                  <option value="nextGacha">Next Gacha</option>
                  <option value="gachaEfficiency">Gacha Eff.</option>
                  <option value="passHolder"> Pass Holder </option>
                  <option value="explorerPoints"> Explorer Points</option>
                </optgroup>

                <optgroup label="Combat">
                  <option value="attackPoints">Attack</option>
                  <option value="defensePoints">Defense</option>
                  <option value="atkLevel">Atk Lvl</option>
                  <option value="defLevel">Def Lvl</option>
                  <option value="petWins">Wins</option>
                  <option value="winsAvailable">Training Wins</option>
                  <option value="monstersCaught">Monsters</option>
                </optgroup>

                <optgroup label="Timers">
                  <option value="timeUntilStarving">Hunger</option>
                  <option value="timeUntilAttack">Bonk</option>
                  <option value="timeUntilWheel">Spin</option>
                  <option value="atkTrainingEndsTimestamp">Atk Training</option>
                  <option value="defTrainingEndsTimestamp">Def Training</option>
                </optgroup>
              </select>

              <button onClick={toggleSortOrder} className="ml-2 mr-[.2rem] mt-[0.1rem]">
                <img
                  src={
                    sortOrder === "desc"
                      ? isDarkMode
                        ? "/imgs/descendingDark.svg"
                        : "/imgs/descending.svg"
                      : isDarkMode
                        ? "/imgs/ascendingDark.svg"
                        : "/imgs/ascending.svg"
                  }
                  alt={sortOrder === "desc" ? "Descending" : "Ascending"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        className={`fixed right-0 top-[7rem] z-10 h-[calc(100vh-7rem)] w-[12.5rem] transform overflow-y-auto border-l-[1.5px]  p-4 shadow-xl transition-transform ${isDarkMode ? "border-darkgreen bg-darkblack" : "border-brown bg-green"} ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        tabIndex={-1}
        onMouseLeave={() => !isTouchDevice && setIsMenuOpen(false)}
      >
        <ul>
          <li>
            <button
              className={`block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "timerViewer" || selectedViewer === "statViewer"  || selectedViewer === "petPasses"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "timerViewer" || selectedViewer === "statViewer"  || selectedViewer === "petPasses"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => {
                const savedDefault = localStorage.getItem("defaultView") || "timerViewer";
                setSelectedViewer(savedDefault);
              }}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
                  alt=""
                  className="mr-[.6rem] mt-[0.11rem] h-5 w-5"
                />
                Pets
              </div>
            </button>
          </li>
          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                  hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "calculator"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "calculator"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("calculator")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/calculatorDark.svg" : "/imgs/calculator.svg"}
                  alt=""
                  className="mr-1 h-6 w-6"
                />
                EV Calculator
              </div>
            </button>
          </li>
          <li>
            <button
              className={`mb-2 mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "charts"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "charts"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("charts")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/chartDark.svg" : "/imgs/chart.svg"}
                  alt=""
                  className="mr-2 mt-[0.11rem] h-5 w-5"
                />
                Charts
              </div>
            </button>
          </li>
          <li>
            <button
              className={`mb-3 mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "profile"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "profile"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("profile")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/profileDark.svg" : "/imgs/profile.svg"}
                  alt=""
                  className="mr-[.6rem] mt-[0.11rem] h-5 w-5"
                />
                Profile
              </div>
            </button>
          </li>
          <hr
            className={`${isDarkMode ? "border-darkgreen" : "border-brown"} -mx-4 w-[calc(100%+2rem)]`}
          />
          <ul className="relative">
            <li>
              <button
                className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "stats"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "stats"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
                onClick={() => setSelectedViewer("stats")}
              >
                <div className="flex flex-row">
                  <img
                    src={isDarkMode ? "/imgs/passBadgeDark.png" : "/imgs/passBadge.png"}
                    alt=""
                    className="mr-[.6rem] mt-[0.11rem] h-5 w-5"
                  />
                  Stats
                </div>
              </button>
            </li>
            <li>
              <button
                className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "whitelist"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "whitelist"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
                onClick={() => setSelectedViewer("whitelist")}
              >
                <div className="flex flex-row">
                  <img
                    src={svgMap.whitelist}
                    alt=""
                    className="mr-[.6rem] mt-[0.11rem] h-5 w-5"
                  />
                  Whitelist
                </div>
              </button>
            </li>
            <li>
              <button
                className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "store"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "store"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
                onClick={() => setSelectedViewer("store")}
              >
                <div className="flex flex-row">
                  <img
                    src={isDarkMode ? "/imgs/storeDark.svg" : "/imgs/store.svg"}
                    alt=""
                    className="mr-2 mt-[0.1rem] h-5 w-5"
                  />
                  Fren Shop
                </div>
              </button>
            </li>
            <li className="mt-2 mb-3">
              <button
                type="button"
                className={`group flex w-full items-center rounded-lg p-2 text-base transition duration-75 hover:border
                ${
                  isDarkMode
                    ? `
                      border-darkgreen
                      bg-darkblack
                      text-white
                      hover:bg-darkblue
                       hover:[box-shadow:inset_0-2px_#37b764]
                      ${
                        [
                          "toppets",
                          "topoldpets",
                          "topcats",
                          "topdefense",
                          "topattack",
                          "topstars",
                          "topdragons",
                          "topdogs",
                          "topsheep",
                          "topmonkeys",
                          "toppandas",
                          "toppepes",
                          "toppenguins",
                        ].includes(selectedViewer)
                          ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                          : ""
                      }
                    `
                    : `
                      border-brown
                      text-brown
                      hover:bg-white
                      hover:[box-shadow:inset_0_-2px_#342e2e]
                      ${
                        ["toppets", "topcats", "topdefense", "topattack", "topoldpets"].includes(
                          selectedViewer,
                        )
                          ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                          : ""
                      }
                    `
                }`}
                aria-controls="dropdown-leaderboard"
                onClick={toggleDropdown}
              >
                <div className="flex flex-row">
                  <img
                    src={isDarkMode ? "/imgs/rankDark.svg" : "/imgs/rank.svg"}
                    alt=""
                    className="mr-[.6rem] mt-[0.05rem] h-5 w-5"
                  />
                  Leaderboards
                </div>
              </button>
              <ul
                id="dropdown-leaderboard"
                className={`${dropdownOpen ? "block" : "hidden"} space-y-2 py-2`}
              >
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "toppets"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "toppets"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("toppets");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Pets
                    </div>
                  </button>
                </li>
                {/*
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topoldpets"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topoldpets"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topoldpets");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/hourglassDark.svg" : "/imgs/hourglass.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Old Pets
                    </div>
                  </button>
                </li>
                */}
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topdogs"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topdogs"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topdogs");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/dogDark.svg" : "/imgs/dog.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Dogs
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topsheep"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topsheep"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topsheep");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/sheepDark.svg" : "/imgs/sheep.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Sheep
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topmonkeys"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topmonkeys"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topmonkeys");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/monkeyDark.svg" : "/imgs/monkey.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4 scale-x-[-1] transform"
                      />
                      Monkeys
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "toppandas"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "toppandas"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("toppandas");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/pandaDark.svg" : "/imgs/panda.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Pandas
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "toppepes"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "toppepes"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("toppepes");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Pepes
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "toppenguins"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "toppenguins"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("toppenguins");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/penguinDark.svg" : "/imgs/penguin.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4 scale-x-[-1] transform"
                      />
                      Penguins
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topcats"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topcats"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topcats");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/catDark.svg" : "/imgs/cat.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Cats
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topdragons"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topdragons"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topdragons");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/dragonDark.svg" : "/imgs/dragon.svg"}
                        alt=""
                        className="mr-2 mt-[0.25rem] h-4 w-4"
                      />
                      Dragons
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    className={`w-26 group ml-9 flex items-center rounded-lg p-2 text-base transition duration-75 hover:border
                      ${
                        isDarkMode
                          ? `
                            border-darkgreen
                            text-white
                            hover:bg-darkblue
                             hover:[box-shadow:inset_0-2px_#37b764]
                            ${
                              selectedViewer === "topstars"
                                ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                                : ""
                            }
                          `
                          : `
                            border-brown
                            text-brown
                            hover:bg-white
                            hover:[box-shadow:inset_0_-2px_#342e2e]
                            ${
                              selectedViewer === "topstars"
                                ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                                : ""
                            }
                          `
                      }`}
                    onClick={() => {
                      setSelectedViewer("topstars");
                      toggleDropdown();
                    }}
                  >
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/starDark.svg" : "/imgs/star.svg"}
                        alt=""
                        className="mr-[.35rem] mt-[0.25rem] h-4 w-4"
                      />
                      Stars
                    </div>
                  </button>
                </li>
              </ul>
            </li>
          </ul>
          {/*
          <li>
            <button
              className={`mb-3 mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "activityfeed"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "activityfeed"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("activityfeed")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/rssDark.svg" : "/imgs/rss.svg"}
                  alt=""
                  className="mr-[.7rem] mt-[0.18rem] h-4 w-4"
                />
                Activity Feed
              </div>
            </button>
          </li>
          */}
          <hr
            className={`${isDarkMode ? "border-darkgreen" : "border-brown"}  -mx-4 w-[calc(100%+2rem)]`}
          />
          <li>
            <button
              className={`mt-3 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "settings"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]

                  ${
                    selectedViewer === "settings"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("settings")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/settingsDark.svg" : "/imgs/settings.svg"}
                  alt=""
                  className="mr-2 mt-[0.12rem] h-5 w-5"
                />
                Settings
              </div>
            </button>
          </li>

          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "resources"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "resources"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("resources")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/toolboxDark.svg" : "/imgs/toolbox.svg"}
                  alt=""
                  className="mr-2 mt-[0.14rem] h-5 w-5"
                />
                Resources
              </div>
            </button>
          </li>

          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "downloads"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "downloads"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("downloads")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/downloadDark.svg" : "/imgs/download.svg"}
                  alt=""
                  className="mr-2 mt-[0.14rem] h-5 w-5"
                />
                Downloads
              </div>
            </button>
          </li>

          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "legend"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "legend"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("legend")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/legendDark.svg" : "/imgs/legend.svg"}
                  alt=""
                  className="mr-2 mt-[0.12rem] h-5 w-5"
                />
                Legend
              </div>
            </button>
          </li>
          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "changelog"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "changelog"
                      ? "border border-brown bg-white  [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("changelog")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/clipboardDark.svg" : "/imgs/clipboard.svg"}
                  alt=""
                  className="mr-2 mt-[0.12rem] h-5 w-5"
                />
                Change Log
              </div>
            </button>
          </li>
          <li>
            <button
              className={`mt-2 block rounded-lg px-2 py-2 hover:border
              ${
                isDarkMode
                  ? `
                  border-darkgreen
                  bg-darkblack
                  text-white
                  hover:bg-darkblue
                   hover:[box-shadow:inset_0-2px_#37b764]
                  ${
                    selectedViewer === "donate"
                      ? "border border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                      : ""
                  }
                `
                  : `
                  border-brown
                  text-brown
                  hover:bg-white
                  hover:[box-shadow:inset_0_-2px_#342e2e]
                  ${
                    selectedViewer === "donate"
                      ? "border border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
                      : ""
                  }
                `
              }`}
              onClick={() => setSelectedViewer("donate")}
            >
              <div className="flex flex-row">
                <img
                  src={isDarkMode ? "/imgs/heartDark.svg" : "/imgs/heart.svg"}
                  alt=""
                  className="mr-2 mt-[0.12rem] h-5 w-5"
                />
                Donate
              </div>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
